import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Clock, Lock, ArrowLeft, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [resetComplete, setResetComplete] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/auth/verify-reset-token/${token}`);
        setTokenValid(true);
        setUserInfo(response.data);
      } catch (error) {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };
    
    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setResetComplete(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-slate-600">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  // Invalid or expired token
  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen flex">
        {/* Left side - Branding */}
        <div className="hidden lg:flex lg:w-1/2 bg-[hsl(243,65%,20%)] text-white p-12 flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
              <Clock className="w-6 h-6" />
            </div>
            <span className="text-2xl font-bold font-['Manrope']">ClockIn Pro</span>
          </div>
          
          <div>
            <h1 className="text-5xl font-bold font-['Manrope'] leading-tight">
              Link expired<br />or invalid
            </h1>
            <p className="mt-6 text-lg text-white/70 max-w-md">
              Password reset links expire after 1 hour for security reasons. 
              Please request a new one.
            </p>
          </div>
          
          <div className="flex items-center gap-6 text-sm text-white/60">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4" />
              Security First
            </div>
          </div>
        </div>

        {/* Right side - Error */}
        <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
          <Card className="w-full max-w-md border-0 shadow-2xl">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <CardTitle className="text-2xl font-['Manrope']">Invalid Reset Link</CardTitle>
              <CardDescription className="mt-2">
                This password reset link is invalid or has expired. Please request a new one.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-4">
                <Link to="/forgot-password">
                  <Button className="w-full bg-[hsl(243,65%,20%)] hover:bg-[hsl(243,65%,25%)]">
                    Request New Reset Link
                  </Button>
                </Link>
                
                <Link to="/login">
                  <Button variant="ghost" className="w-full gap-2">
                    <ArrowLeft className="w-4 h-4" />
                    Back to Login
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Reset complete
  if (resetComplete) {
    return (
      <div className="min-h-screen flex">
        {/* Left side - Branding */}
        <div className="hidden lg:flex lg:w-1/2 bg-[hsl(243,65%,20%)] text-white p-12 flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
              <Clock className="w-6 h-6" />
            </div>
            <span className="text-2xl font-bold font-['Manrope']">ClockIn Pro</span>
          </div>
          
          <div>
            <h1 className="text-5xl font-bold font-['Manrope'] leading-tight">
              Password<br />reset!
            </h1>
            <p className="mt-6 text-lg text-white/70 max-w-md">
              Your password has been successfully reset. 
              You can now log in with your new password.
            </p>
          </div>
          
          <div className="flex items-center gap-6 text-sm text-white/60">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Success
            </div>
          </div>
        </div>

        {/* Right side - Success */}
        <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
          <Card className="w-full max-w-md border-0 shadow-2xl">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <CardTitle className="text-2xl font-['Manrope']">Password Reset Complete</CardTitle>
              <CardDescription className="mt-2">
                Your password has been successfully reset. You can now log in with your new password.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <Link to="/login">
                <Button className="w-full bg-[hsl(243,65%,20%)] hover:bg-[hsl(243,65%,25%)]">
                  Continue to Login
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Reset form
  return (
    <div className="min-h-screen flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[hsl(243,65%,20%)] text-white p-12 flex-col justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
            <Clock className="w-6 h-6" />
          </div>
          <span className="text-2xl font-bold font-['Manrope']">ClockIn Pro</span>
        </div>
        
        <div>
          <h1 className="text-5xl font-bold font-['Manrope'] leading-tight">
            Create a new<br />password
          </h1>
          <p className="mt-6 text-lg text-white/70 max-w-md">
            Choose a strong password that you haven't used before. 
            Your password should be at least 6 characters.
          </p>
        </div>
        
        <div className="flex items-center gap-6 text-sm text-white/60">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4" />
            Secure Reset
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <Card className="w-full max-w-md border-0 shadow-2xl">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl font-['Manrope']">Reset Your Password</CardTitle>
            <CardDescription>
              {userInfo && (
                <>Resetting password for <strong>{userInfo.email}</strong></>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">New Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter new password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="reset-password-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  data-testid="reset-confirm-password-input"
                />
              </div>
              
              <div className="text-xs text-slate-500">
                Password must be at least 6 characters long.
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-[hsl(243,65%,20%)] hover:bg-[hsl(243,65%,25%)]"
                disabled={loading}
                data-testid="reset-submit-btn"
              >
                {loading ? (
                  <div className="spinner" />
                ) : (
                  'Reset Password'
                )}
              </Button>
              
              <Link to="/login" className="block">
                <Button variant="ghost" className="w-full gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Back to Login
                </Button>
              </Link>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
